import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import Badge from '../../components/base/Badge';
import ProgressBar from '../../components/base/ProgressBar';
import { useAuth } from '../../context/useAuth';
import { useProgress } from '../../context/ProgressContext';
import { enrollCourseApi, getCourseById, getMyEnrollmentsApi } from '../../api/apiServices';
import CourseReviewsTab from './components/CourseReviewsTab';

const levelColorMap = {
  Beginner: 'teal',
  Intermediate: 'amber',
  Advanced: 'rose',
};

export default function CourseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { getEnrollment, getLessonProgress, isEnrolled, enroll } = useProgress();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [isEnrolledLocal, setIsEnrolledLocal] = useState(false);
  const [enrollmentLocal, setEnrollmentLocal] = useState(null);

  // Fetch course + check enrollment status
  useEffect(() => {
    const loadData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch course
        const courseRes = await getCourseById(id);
        if (courseRes.success && courseRes.course) {
          setCourse(courseRes.course);

          // Check if user is enrolled (using backend)
          if (isAuthenticated) {
            try {
              const enrollRes = await getMyEnrollmentsApi();
              if (enrollRes.success && Array.isArray(enrollRes.enrollments)) {
                const found = enrollRes.enrollments.find(e => 
                  String(e.courseId?._id || e.courseId) === String(id)
                );

                setIsEnrolledLocal(!!found);
                setEnrollmentLocal(found || null);
              }
            } catch (enrollErr) {
              console.error("Failed to check enrollment:", enrollErr);
              setIsEnrolledLocal(false);
            }
          }
        } else {
          setError(courseRes.message || "Course not found");
        }
      } catch (err) {
        console.error("Failed to fetch course:", err);
        setError("Failed to load course details");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, isAuthenticated]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading course details...</p>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <p className="text-xl font-bold text-slate-700 mb-2">Course not found</p>
          <p className="text-slate-500 mb-6">{error || "The course you're looking for doesn't exist."}</p>
          <Link to="/courses" className="text-teal-600 hover:text-teal-700 font-medium">
            ← Back to All Courses
          </Link>
        </div>
      </div>
    );
  }

  const courseId = course._id || course.id;

  // Calculate total lessons
  const totalLessons = course.sections?.reduce((total, section) => {
    return total + (section.lectures?.length || 0);
  }, 0) || (course.lessons?.length || 0);

  const liveEnrollment = getEnrollment(courseId) || enrollmentLocal;
  const isEnrolledStatus = isEnrolled(courseId) || isEnrolledLocal;

  const progress = liveEnrollment?.courseCompleted
    ? 100
    : getLessonProgress(courseId, totalLessons);

  const completedLessonsCount = Array.isArray(liveEnrollment?.completedLessons)
    ? liveEnrollment.completedLessons.length
    : 0;

  const lastLessonId = liveEnrollment?.lastLessonId || 
    course.sections?.[0]?.lectures?.[0]?._id || course.sections?.[0]?.lectures?.[0]?.id || '';

  // Real Enroll Function
  const handleEnroll = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setEnrolling(true);

    try {
      await enroll(courseId);
      setIsEnrolledLocal(true);
      const res = await getCourseById(courseId);
      if (res.success && res.course) setCourse(res.course);
    } catch (err) {
      console.error("Enroll failed:", err);
      alert(err.response?.data?.message || "Failed to enroll. Please try again.");
    } finally {
      setEnrolling(false);
    }
  };

  const handleStartLearning = () => {
    if (!isEnrolledStatus) return;

    if (lastLessonId) {
      navigate(`/courses/${courseId}/lesson/${lastLessonId}`);
    } else if (course?.sections?.[0]?.lectures?.[0]?._id) {
      const firstLessonId = course.sections[0].lectures[0]._id;
      navigate(`/courses/${courseId}/lesson/${firstLessonId}`);
    } else if (course?.lessons?.[0]?._id) {
      navigate(`/courses/${courseId}/lesson/${course.lessons[0]._id}`);
    }
  };

  const totalQuizzes = totalLessons + (course?.finalQuiz ? 1 : 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="pt-16">
        {/* Hero Section */}
        <div className="bg-slate-900 text-white">
          <div className="max-w-7xl mx-auto px-6 py-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2">
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge label={course.level} variant={levelColorMap[course.level] || 'slate'} />
                  <Badge label={course.category?.replace('-', ' ') || 'General'} variant="teal" />
                </div>
                <h1 className="text-3xl font-bold leading-tight mb-3">{course.title}</h1>
                <p className="text-slate-300 leading-relaxed mb-5">{course.description}</p>

                <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
                  <button
                    onClick={() => setActiveTab('reviews')}
                    className="flex items-center gap-1.5 hover:text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-star-fill text-amber-400" />
                    <strong className="text-white">{course.rating ? Number(course.rating).toFixed(1) : '4.8'}</strong> 
                    <span className="underline decoration-slate-600 underline-offset-2">
                      ({course.reviewsCount ? `${course.reviewsCount} reviews` : `${(course.studentsEnrolled || 0).toLocaleString()} students`})
                    </span>
                  </button>
                  <span className="flex items-center gap-1.5"><i className="ri-time-line" />{course.duration}</span>
                  <span className="flex items-center gap-1.5"><i className="ri-book-open-line" />{totalLessons} lessons</span>
                  <span className="flex items-center gap-1.5"><i className="ri-quiz-line" />{totalQuizzes} quizzes</span>
                </div>

                <div className="flex items-center gap-2 mt-6">
                  {course.instructorAvatar && (
                    <img src={course.instructorAvatar} alt={course.instructor} className="w-8 h-8 rounded-full object-cover border border-teal-400" />
                  )}
                  <span className="text-sm text-slate-300">
                    By <strong className="text-white">{course.instructor}</strong>
                  </span>
                </div>
              </div>

              {/* Enroll / Continue Sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 sticky top-20">
                  <div className="relative h-44">
                    <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover object-top" />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <div className="w-14 h-14 flex items-center justify-center bg-white/90 rounded-full">
                        <i className="ri-play-fill text-teal-600 text-3xl" />
                      </div>
                    </div>
                  </div>

                    <div className="p-5">
                    {isEnrolledStatus ? (
                      <>
                        <ProgressBar value={progress} showLabel size="md" className="mb-4" />
                        <p className="text-xs text-slate-500 mb-4 font-medium">
                          {completedLessonsCount}/{totalLessons || completedLessonsCount} lessons completed ({progress}%)
                        </p>
                        <button 
                          onClick={handleStartLearning} 
                          className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-sm"
                        >
                          <i className="ri-play-circle-line text-lg" />
                          {progress === 100 ? 'Review Completed Course' : (progress > 0 ? 'Continue Learning' : 'Start Learning')}
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="mb-1">
                          {!course.isPaid || !course.price || course.price === 0 ? (
                            <div className="flex items-center gap-2">
                              <span className="text-3xl font-extrabold text-emerald-600">Free</span>
                              <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                100% Free Access
                              </span>
                            </div>
                          ) : course.hasDiscount && course.discount > 0 ? (
                            <div>
                              <div className="flex items-baseline gap-2">
                                <span className="text-3xl font-extrabold text-slate-900">
                                  ${(course.price * (1 - (course.discount / 100))).toFixed(2)}
                                </span>
                                <span className="text-slate-400 line-through text-lg">
                                  ${Number(course.price).toFixed(2)}
                                </span>
                                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">
                                  {course.discount}% OFF
                                </span>
                              </div>
                              <p className="text-[11px] text-amber-700 font-semibold mt-0.5">
                                Limited time discounted price
                              </p>
                            </div>
                          ) : (
                            <div className="flex items-baseline gap-2">
                              <span className="text-3xl font-extrabold text-slate-900">${course.price}</span>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mb-5">Lifetime access • Certificate included</p>
                        <button 
                          onClick={handleEnroll} 
                          disabled={enrolling}
                          className="w-full py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap shadow-md shadow-teal-900/10"
                        >
                          {enrolling ? 'Enrolling...' : (!course.isPaid || !course.price ? 'Enroll for Free' : 'Enroll Now')}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              {/* Tabs */}
              <div className="flex gap-1 mb-6 bg-white rounded-xl p-1 border border-slate-100 w-fit overflow-x-auto max-w-full">
                {[
                  { id: 'overview', label: 'Overview', icon: 'ri-file-list-line' },
                  { id: 'curriculum', label: 'Curriculum', icon: 'ri-book-open-line' },
                  { id: 'instructor', label: 'Instructor', icon: 'ri-user-star-line' },
                  { id: 'reviews', label: 'Reviews & Feedback', icon: 'ri-star-line' }
                ].map(tab => (
                  <button 
                    key={tab.id} 
                    onClick={() => setActiveTab(tab.id)} 
                    className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      activeTab === tab.id 
                        ? 'bg-teal-600 text-white shadow-sm' 
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    <i className={tab.icon} />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="bg-white rounded-xl border border-slate-100 p-6">
                    <h3 className="font-bold text-slate-900 mb-4">What you'll learn</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {(course.whatYouLearn || []).map((item, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <i className="ri-check-line text-teal-500 mt-0.5" />
                          <span className="text-sm text-slate-600">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-100 p-6">
                    <h3 className="font-bold text-slate-900 mb-3">Description</h3>
                    <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                      {course.longDescription || course.description}
                    </p>
                  </div>

                  {course.requirements?.length > 0 && (
                    <div className="bg-white rounded-xl border border-slate-100 p-6">
                      <h3 className="font-bold text-slate-900 mb-4">Requirements</h3>
                      <ul className="space-y-2">
                        {course.requirements.map((req, i) => (
                          <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                            <i className="ri-circle-fill text-slate-300 mt-1.5 text-xs" />
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Curriculum Tab */}
              {activeTab === 'curriculum' && (
                <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                  <div className="p-6 border-b">
                    <h3 className="font-bold text-slate-900">
                      Course Curriculum — {totalLessons} Lessons
                    </h3>
                  </div>

                  {course.sections && course.sections.length > 0 ? (
                    course.sections.map((section, sectionIndex) => (
                      <div key={section._id || sectionIndex} className="border-b last:border-0">
                        <div className="px-6 py-4 bg-slate-50 font-medium text-slate-800 flex items-center gap-3">
                          <span>Section {sectionIndex + 1}:</span> {section.title}
                        </div>
                        <div className="divide-y">
                          {(section.lectures || []).map((lesson, lessonIndex) => {
                            const isCompleted = enrollmentLocal?.completedLessons?.includes(lesson._id);
                            const isExpanded = expandedLesson === lesson._id;

                            return (
                              <div key={lesson._id} className="px-6 py-4">
                                <button 
                                  onClick={() => setExpandedLesson(isExpanded ? null : lesson._id)}
                                  className="w-full flex items-center gap-4 text-left hover:bg-slate-50 -mx-2 px-2 py-2 rounded-xl group"
                                >
                                  <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${isCompleted ? 'bg-teal-100 text-teal-600' : 'bg-slate-100 text-slate-500'}`}>
                                    {isCompleted ? '✓' : lessonIndex + 1}
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-slate-800 group-hover:text-teal-600 transition-colors">
                                      {lesson.title}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-0.5 capitalize">
                                      {lesson.type} • {lesson.duration || '—'}
                                    </p>
                                  </div>
                                  <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-slate-400`} />
                                </button>

                                {isExpanded && (
                                  <div className="ml-10 mt-2 text-sm text-slate-600">
                                    {lesson.quiz && lesson.quiz.questions?.length > 0 && (
                                      <p className="text-teal-600 text-xs font-medium flex items-center gap-1">
                                        <i className="ri-quiz-line" /> Includes quiz ({lesson.quiz.questions.length} questions)
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-12 text-center text-slate-500">
                      No curriculum available yet.
                    </div>
                  )}

                  {course.finalQuiz && (
                    <div className="px-6 py-5 bg-amber-50 flex items-center gap-4 border-t">
                      <div className="w-8 h-8 bg-amber-100 rounded-xl flex items-center justify-center">
                        <i className="ri-trophy-line text-amber-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">Final Course Quiz</p>
                        <p className="text-xs text-slate-500">
                          {course.finalQuiz.questions?.length || 0} questions • Required for certificate
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Instructor Tab */}
              {activeTab === 'instructor' && (
                <div className="bg-white rounded-xl border border-slate-100 p-6">
                  <div className="flex items-start gap-4">
                    {course.instructorAvatar && (
                      <img 
                        src={course.instructorAvatar} 
                        alt={course.instructor} 
                        className="w-20 h-20 rounded-2xl object-cover" 
                      />
                    )}
                    <div>
                      <h3 className="font-bold text-xl text-slate-900">{course.instructor}</h3>
                      <p className="text-slate-600 mt-1">{course.instructorBio}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Reviews Tab */}
              {activeTab === 'reviews' && (
                <CourseReviewsTab courseId={courseId} isEnrolled={isEnrolledLocal} />
              )}
            </div>

            {/* Sidebar - Tags & Related */}
            <div className="hidden lg:block">
              <div className="bg-white rounded-2xl border border-slate-100 p-6 sticky top-24">
                <h4 className="font-bold text-slate-800 mb-3">Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {(course.tags || []).map((tag, i) => (
                    <span key={i} className="text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}